import { useQuery } from '@tanstack/react-query';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { FilterBar } from '@/components/common/FilterBar';
import { filterDataApi, filterDataPaged } from '@/api/services/filterData';
import type { NopFiltersDto } from '@/api/types/riskDashboard';

// The page's filter state: the Nop hub DTO fields plus the UI-only mode toggles.
export interface NopFilterState {
  login: number | null;
  uniqueId: string | null;
  platformId: number | null;
  instrument: string | null;
  mifidFilter: string | null;
  groupName: string | null;
  groupTextFilter: string | null;
  portfolioId: number | null;
  symbolMapping: boolean; // Auto Map
  byUniqueId: boolean;
  ccyCurrency: boolean; // CCY
}

export function defaultNopFilters(): NopFilterState {
  return {
    login: null,
    uniqueId: null,
    platformId: null,
    instrument: null,
    mifidFilter: null,
    groupName: null,
    groupTextFilter: null,
    portfolioId: null,
    symbolMapping: false,
    byUniqueId: false,
    ccyCurrency: false,
  };
}

export function toNopFilters(f: NopFilterState): NopFiltersDto {
  return {
    // By Unique ID swaps which field carries the single ID input.
    login: f.byUniqueId ? null : f.login,
    uniqueId: f.byUniqueId ? f.uniqueId : null,
    platformId: f.platformId,
    ccyCurrency: f.ccyCurrency,
    instrument: f.instrument,
    mifidFilter: f.mifidFilter,
    groupName: f.groupName,
    groupTextFilter: f.groupTextFilter,
    portfolioId: f.portfolioId,
    requestedCurrency: null,
    symbolMapping: f.symbolMapping,
  };
}

interface Props {
  draft: NopFilterState;
  setDraft: React.Dispatch<React.SetStateAction<NopFilterState>>;
  onFetch: () => void;
  onClear: () => void;
  onModeToggle: (key: 'symbolMapping' | 'byUniqueId' | 'ccyCurrency', value: boolean) => void;
  onOpenThresholds: () => void;
  canAutoMap: boolean;
  canByUniqueId: boolean;
  canByCcy: boolean;
  canThresholds: boolean;
}

export function NopFilterBar({
  draft,
  setDraft,
  onFetch,
  onClear,
  onModeToggle,
  onOpenThresholds,
  canAutoMap,
  canByUniqueId,
  canByCcy,
  canThresholds,
}: Props) {
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getEnabledConnectors, staleTime: 5 * 60_000 });
  const mifid = useQuery({ queryKey: ['rd-filter', 'mifid'], queryFn: filterDataApi.getMifidClassifications, staleTime: 5 * 60_000 });
  const portfolios = useQuery({ queryKey: ['rd-filter', 'portfolios'], queryFn: filterDataApi.getPortfolios, staleTime: 5 * 60_000 });

  const str = (v: string | number | null) => (v == null ? '' : String(v));
  // Portfolio exclusivity: a chosen portfolio greys + disables the other scoping filters.
  const lock = draft.portfolioId != null ? 'pe-none opacity-50' : '';
  const lk = (node: React.ReactNode) => <div className={lock}>{node}</div>;
  // By Unique ID hides connector/MIFID/group/group-text and relabels the ID input.
  const uid = draft.byUniqueId;

  const fields: React.ReactNode[] = [
    lk(
      <>
        <label className="form-label">{uid ? 'Unique ID' : 'Login'}</label>
        {uid ? (
          <input className="form-control" value={draft.uniqueId ?? ''} onChange={(e) => setDraft((f) => ({ ...f, uniqueId: e.target.value || null }))} />
        ) : (
          <input className="form-control" inputMode="numeric" value={draft.login ?? ''} onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value ? Number(e.target.value) : null }))} />
        )}
      </>,
    ),
    lk(
      <>
        <label className="form-label">Instrument</label>
        <input className="form-control" value={draft.instrument ?? ''} onChange={(e) => setDraft((f) => ({ ...f, instrument: e.target.value || null }))} />
      </>,
    ),
    ...(!uid
      ? [
          lk(<Select2 label="Connector" allowClear placeholder="All" value={str(draft.platformId)} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, platformId: v ? Number(v) : null }))} />),
          lk(<Select2 label="MiFID" allowClear placeholder="All" value={str(draft.mifidFilter)} options={mifid.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, mifidFilter: v || null }))} />),
          lk(
            <>
              <label className="form-label">Group</label>
              <Select2Async placeholder="All" value={str(draft.groupName) || null} fetch={filterDataPaged.groups} onChange={(v) => setDraft((f) => ({ ...f, groupName: v || null }))} />
            </>,
          ),
          lk(
            <>
              <label className="form-label">Group Text Filter</label>
              <input className="form-control" placeholder="e.g. S*, *abc*, !SPOT" value={draft.groupTextFilter ?? ''} onChange={(e) => setDraft((f) => ({ ...f, groupTextFilter: e.target.value || null }))} />
            </>,
          ),
        ]
      : []),
    <Select2 key="portfolio" label="Portfolio Profile" allowClear placeholder="All" value={str(draft.portfolioId)} options={portfolios.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, portfolioId: v ? Number(v) : null }))} />,
  ];

  return (
    <FilterBar
      fields={fields}
      visibleCount={4}
      modes={
        <>
          {canAutoMap && <ModeSwitch label="Auto Map" on={draft.symbolMapping} onToggle={(c) => onModeToggle('symbolMapping', c)} />}
          {canByUniqueId && <ModeSwitch label="By UID" on={draft.byUniqueId} onToggle={(c) => onModeToggle('byUniqueId', c)} />}
          {canByCcy && <ModeSwitch label="CCY" on={draft.ccyCurrency} onToggle={(c) => onModeToggle('ccyCurrency', c)} />}
        </>
      }
      actions={
        <>
          {canThresholds && (
            <button type="button" className="btn btn-sm btn-outline-primary" title="NOP Thresholds" onClick={onOpenThresholds}>
              <i className="fa fa-bell me-1" />
              NOP Thresholds
            </button>
          )}
          <button type="button" className="btn btn-primary btn-sm" onClick={onFetch}>
            <i className="fa fa-search me-1" />
            Fetch
          </button>
          <button type="button" className="btn btn-link btn-sm" onClick={onClear}>
            Clear All Filters
          </button>
        </>
      }
    />
  );
}

// NOP mode toggles are SWITCHES (not RD-style buttons).
function ModeSwitch({ label, on, onToggle }: { label: string; on: boolean; onToggle: (c: boolean) => void }) {
  return (
    <div className="form-check form-switch mb-0">
      <input className="form-check-input" type="checkbox" role="switch" checked={on} onChange={(e) => onToggle(e.target.checked)} id={`nop-switch-${label.replace(/\s+/g, '')}`} />
      <label className="form-check-label small" htmlFor={`nop-switch-${label.replace(/\s+/g, '')}`}>
        {label}
      </label>
    </div>
  );
}
